import { Component, OnInit, AfterViewInit/*, OnDestroy*/ } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
// https://www.npmjs.com/package/@ngx-pwa/offline
import { Network } from '@ngx-pwa/offline';

import { GenericRestService } from '@common/services/rest/generic-rest.service';

import { environment as env_const } from '@env/environment';

import { registerLocaleData } from '@angular/common';
import localeSpanish from '@angular/common/locales/es';
import localeEuskera from '@angular/common/locales/eu';
import localeEnglish from '@angular/common/locales/en';
import localeGreek from '@angular/common/locales/el';
import localeArabic from '@angular/common/locales/ar';
import localezhHans from '@angular/common/locales/zh-Hans';
import { MatIconRegistry } from '@angular/material/icon';

import {forkJoin, Observable} from 'rxjs';

const handleConnection = () => {
  if (navigator.onLine) {
    isReachable(getServerUrl()).then((online) => {
      if (online) {
        // handle online status
        document.body.classList.remove('offline');
        console.log('%cKAYROX is ONLINE', 'color:black;background:green');
      } else {
        document.body.classList.add('offline');
        console.log(
          '%cKAYROX isn´t connectivity',
          'color:black;background:yellow'
        );
      }
    });
  } else {
    // handle offline status
    document.body.classList.add('offline');
    console.log('%cKAYROX is OFFLINE', 'color:black;background:orange');
  }
};

const isReachable = (url: string) => {
  /**
   * Note: fetch() still "succeeds" for 404s on subdirectories,
   * which is ok when only testing for domain reachability.
   *
   * Example:
   *   https://google.com/noexist does not throw
   *   https://noexist.com/noexist does throw
   */
  return fetch(url, { method: 'HEAD', mode: 'no-cors' })
    .then(function (resp) {
      return resp && (resp.ok || resp.type === 'opaque');
    })
    .catch(function (err) {
      console.warn('[conn test failure]:', err);
    });
};

const getServerUrl = (): string => {
  return env_const.serverUrl || window.location.origin;
};

const referenceScreenWidth = 1920; // Cambia esto según tu resolución de referencia

@Component({
  selector: 'tiwp-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit/*, OnDestroy*/ {
  private screenViewScale = 1;

  constructor(
    protected network: Network,
    private translate: TranslateService,
    private titleService: Title,
    private generic: GenericRestService,
    private router: Router,
    protected matIconRegistry: MatIconRegistry,
    protected domSanitizer: DomSanitizer
  ) {
    this.registryIcons();
  }

  preloadAllLanguages(languageKeys: string[]): Observable<any[]> {
    const loaderObservables: Observable<any>[] = languageKeys.map(lang =>
      // Mapeamos cada clave a su Observable de recarga
      this.translate.reloadLang(lang)
    );

    // Observable esperando a que todas las cargas se completen en paralelo
    return forkJoin(loaderObservables);
  }

  private setupLanguage() {
    registerLocaleData(localeSpanish, 'es');
    registerLocaleData(localeEnglish, 'en');
    registerLocaleData(localeEuskera, 'eus');
    registerLocaleData(localeGreek, 'el');
    registerLocaleData(localeArabic, 'ar');
    registerLocaleData(localezhHans, 'zh-Hans');

    let defaultLanguage = this.translate.getBrowserLang() || env_const.defaultLanguage;

    const languages = env_const.languages; // ['es', 'en']
    const availableLanguageKeys = languages.map((language) => language.key);
    this.translate.addLangs(availableLanguageKeys);

    const defaultLanguageIndex = defaultLanguage ? availableLanguageKeys.indexOf(defaultLanguage) : -1;
    if(defaultLanguageIndex === -1) {
      defaultLanguage = env_const.defaultLanguage || 'en';
    }

    // Establece el idioma por defecto y el idioma a usar.
    this.translate.setDefaultLang(defaultLanguage);

    this.preloadAllLanguages(availableLanguageKeys).subscribe({
      next: (results) => {
        this.translate.use(defaultLanguage);
        console.log('✅ Preloaded language files. Stable service.');
      },
      error: (err) => {
        // Error si *cualquiera* de los archivos json de 'language' falla (ej: 404).
        console.error('❌ Error preloading any translation file.', err);
      }
    });
  }

  ngOnInit() {
    this.setupLanguage();

    if (env_const.forcedTitle) {
      this.titleService.setTitle(env_const.forcedTitle);
    } else {
      this.translate.get('appTitle').subscribe(
        (title: string) => this.titleService.setTitle(title),
        (error) => {
          this.titleService.setTitle('KAYROX');
          console.error(
            '%cLanguage error: ',
            'color:black;background:red',
            error.message,
            'Key: ' + 'appTitle'
          );
        }
      );
    }

    /*
        // TODO: assign proper type to event
        this.router.events.subscribe((event: any): void => {
          this.navigationInterceptor(event);
        });
    */
  }


  ngAfterViewInit() {
    const handleNetworkChange = () => {
      if (navigator.onLine) {
        // this.router.navigateByUrl ( env_const.desktopUrl );
        document.body.classList.remove('offline');
        console.log('%cKAYROX is ONLINE', 'color:black;background:green');
      } else {
        this.router.navigateByUrl(env_const.offlineUrl);
        document.body.classList.add('offline');
        console.log('%cKAYROX is OFFLINE', 'color:black;background:orange');
      }
    };

    handleNetworkChange();

    window.addEventListener('online', handleNetworkChange);
    window.addEventListener('offline', handleNetworkChange);
  }

  /*
    navigationInterceptor(event): void {
      if (event instanceof NavigationStart) {
        this.isLoading = true;
      }
      if (event instanceof NavigationEnd) {
        this.isLoading = false;
      }
      if (event instanceof NavigationCancel) {
        this.isLoading = false;
      }
      if (event instanceof NavigationError) {
        this.isLoading = false;
      }
    }
  */

  private registryIcons() {
    this.matIconRegistry.addSvgIcon(
      'tiwp_object_3d',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        env_const.default.uiSpecialIcons.object3d
      )
    );
    this.matIconRegistry.addSvgIcon(
      'tiwp_marker_3d',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        env_const.default.uiSpecialIcons.marker3d
      )
    );
    this.matIconRegistry.addSvgIcon(
      'tiwp_marker_image',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        env_const.default.uiSpecialIcons.markerImage
      )
    );
  }
}
